import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Row, Col, Input, Form } from 'antd';
import { isValidName } from '../../utils';
import successImg from '../../assets/images/successBig.png';
import { FieldError } from 'rc-field-form/es/interface';
import CaptchaMemorial from '../../DLS/CaptchaMemorial';
import { useMemorial } from '../../context/memorial.provider';
import useApi from '../../utils/api';

const ContactUs = () => {
  const [form] = Form.useForm();
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasFormErrors, setHasFormErrors] = useState<boolean>(true);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [showRequiredErrorInCaptcha, setShowRequiredErrorInCaptcha] = useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { id: userId } = useMemorial();
  const { api } = useApi();
  const message = Form.useWatch('message', form);

  useEffect(() => {
    containerRef?.current?.scrollIntoView();
  }, [success]);

  const onFinish = (values: any) => {
    if (userId || captchaToken) {
      const { email, firstName, lastName, message } = form.getFieldsValue();
      setLoading(true);
      api()
        .post('/contact-send-email', { name: firstName, last_name: lastName, email, message })
        .then((response) => {
          if (response.status === 200) {
            setSuccess(true);
          }
        })

        .finally(() => setLoading(false));
    }
  };

  const fieldIsEmpty = (field: FieldError) => {
    let fieldValue = form.getFieldValue(field.name.join('.'));
    return fieldValue === undefined || [].concat(fieldValue).join().trim() === '';
  };

  const checkValidForm = () => {
    const fields = form
      .getFieldsError()
      .filter((field) => fieldIsEmpty(field) || field.errors.length > 0);

    setHasFormErrors(fields.length > 0);
  };

  return (
    <div ref={containerRef}>
      {success ? (
        <div className="flex flex-col items-center px-[40px] md:min-h-[calc(100vh-80px)] md:px-[16px]">
          <img src={successImg} alt="" className="mx-auto mt-[80px] md:mt-[180px]" />
          <h1 className="my-[24px] text-center">{t('contactUs.messageSent')}</h1>
          <p className="mb-[24px] text-center">{t('contactUs.messageSentDescription')}</p>
          <Button
            onClick={() => navigate(userId ? '/home/start?page=1&pageSize=9' : '/')}
            type="primary"
            className="w-full md:w-[initial]"
          >
            {t('contactUs.backToHome')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col justify-between px-[16px] md:min-h-[calc(100vh-80px)]">
          <h1 className="mb-[56px] mt-[24px] hidden text-center md:mt-[40px] md:block">
            {t('contactUs.title')}
          </h1>
          <h4 className="my-[24px] text-center md:hidden">{t('contactUs.title')}</h4>
          <div className="flex flex-1 flex-col justify-between">
            <Form
              onFinish={onFinish}
              form={form}
              onFieldsChange={checkValidForm}
              className="mb-[120px] md:mb-[0px]"
            >
              <Row gutter={[16, 0]}>
                <Col xs={12} sm={12}>
                  <Form.Item
                    required
                    name="firstName"
                    label={t('common.firstName')}
                    rules={[
                      {
                        required: true,
                        message: String(t('common.formsErrors.firstName.required')),
                      },
                      {
                        validator: async (_obj, value) => {
                          if (value && !isValidName(value)) {
                            return Promise.reject(
                              new Error(String(t('common.formsErrors.firstName.valid')))
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={12}>
                  <Form.Item
                    required
                    name="lastName"
                    label={t('common.lastName')}
                    rules={[
                      {
                        required: true,
                        message: String(t('common.formsErrors.lastName.required')),
                      },
                      {
                        validator: async (_obj, value) => {
                          if (value && !isValidName(value)) {
                            return Promise.reject(
                              new Error(String(t('common.formsErrors.lastName.valid')))
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    required
                    name="email"
                    label={t('common.email')}
                    rules={[
                      { required: true, message: String(t('common.formsErrors.email.required')) },
                      {
                        type: 'email',
                        message: String(t('common.formsErrors.email.valid')),
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    required
                    name="message"
                    label={t('common.customMessage')}
                    rules={[
                      { required: true, message: String(t('contactUs.customMessageRequired')) },
                    ]}
                    extra={
                      <p className={`smallParagraph mt-[4px] text-text2`}>
                        {t('common.textAreaExtraText', {
                          currentLength: message?.length || 0,
                          maxLength: 100,
                        })}
                      </p>
                    }
                  >
                    <Input.TextArea placeholder="" rows={5} maxLength={100} />
                  </Form.Item>
                </Col>
                {!userId && (
                  <Col xs={24} className="mb-[40px] md:mb-[0px]">
                    <CaptchaMemorial
                      captchaToken={captchaToken}
                      showRequiredError={showRequiredErrorInCaptcha}
                      setCaptchaToken={(token) => {
                        showRequiredErrorInCaptcha && setShowRequiredErrorInCaptcha(false);
                        setCaptchaToken(token);
                      }}
                    />
                  </Col>
                )}
              </Row>
            </Form>
            <Col xs={24} className="footerButtons flex flex-row justify-end md:py-[32px]">
              <Button
                className={`${
                  hasFormErrors || (!userId && !captchaToken) ? 'primaryDisabled' : ''
                }`}
                htmlType="submit"
                type="primary"
                loading={loading}
                onClick={() => {
                  if (!userId && !captchaToken) {
                    setShowRequiredErrorInCaptcha(true);
                  }
                  form.submit();
                }}
              >
                {t('common.send')}
              </Button>
            </Col>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactUs;
